import { MouseEventHandler, useState } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment, useMutation } from "react-relay";
import { logger } from "@/common/logger";
import { UploadCloud02Icon } from "@/icons";
import { Button, ButtonProps } from "@/kit/ui/button";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { relayErrorMessage } from "@/utils/relay";
import { toast } from "@/utils/toast";
import { WorkspaceVersionPublishButtonFragment$key } from "./__generated__/WorkspaceVersionPublishButtonFragment.graphql";
import { WorkspaceVersionPublishButtonMutation } from "./__generated__/WorkspaceVersionPublishButtonMutation.graphql";

const Fragment = graphql`
  fragment WorkspaceVersionPublishButtonFragment on WorkspaceVersion {
    id
    version
    publishedAt
    viewerCanPublish: viewerCan(action: PUBLISH_WORKSPACE_VERSION)
  }
`;

const Mutation = graphql`
  mutation WorkspaceVersionPublishButtonMutation($id: ID!) {
    publishWorkspaceVersion(id: $id) {
      id
      latest
      publishedAt
      workspace {
        id
        latestVersion {
          id
          version
        }
      }
    }
  }
`;

export interface WorkspaceVersionPublishButtonProps extends ButtonProps {
  version: WorkspaceVersionPublishButtonFragment$key;
}

export default function WorkspaceVersionPublishButton({
  version: versionFragment,
  children,
  ...props
}: WorkspaceVersionPublishButtonProps) {
  const version = useFragment(Fragment, versionFragment);
  const [isOpen, setOpen] = useState(false);
  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspaceVersionPublishButtonMutation>(Mutation);

  if (version.publishedAt || !version.viewerCanPublish) {
    return null;
  }

  const onConfirm: MouseEventHandler = () =>
    commitMutation({
      variables: { id: version.id },
      onCompleted: () => {
        toast.success(
          <FormattedMessage
            defaultMessage="Version {version} has been published"
            values={{ version: version.version }}
          />,
        );
        setOpen(false);
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });

  const onOpenChange = (open: boolean) => {
    if (isMutationInFlight) return;
    setOpen(open);
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveModalTrigger asChild>
        <Button {...props}>
          {children ?? <FormattedMessage defaultMessage="Publish version" />}
        </Button>
      </ResponsiveModalTrigger>

      <ResponsiveModalPortal>
        <ResponsiveModalContent
          className="sm:w-96 w-auto bg-linear-to-b from-[#EFEDFC] from-[-16.39%] to-white to-[65.62%]"
          noClose={isMutationInFlight}
        >
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="bg-brand-100 rounded-md p-2 w-fit">
              <UploadCloud02Icon className="text-brand-500 w-8 h-8" />
            </div>
            <ResponsiveModalTitle>
              <FormattedMessage defaultMessage="Publish workspace version" />
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center w-10/12">
              <FormattedMessage
                defaultMessage="You're about to publish “{version}”. It will become visible to everyone and can no longer be edited."
                values={{ version: version.version }}
              />
            </ResponsiveModalDescription>
            <div className="w-full flex flex-row gap-2 mt-4">
              <ResponsiveModalClose asChild>
                <Button
                  variant="secondary"
                  disabled={isMutationInFlight}
                  className="w-full"
                >
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
              </ResponsiveModalClose>
              <Button
                onClick={onConfirm}
                variant="primary"
                disabled={isMutationInFlight}
                className="w-full"
              >
                <UploadCloud02Icon className="w-5 h-5" />
                <FormattedMessage defaultMessage="Publish" />
              </Button>
            </div>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModalPortal>
    </ResponsiveModal>
  );
}
